import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Anchor, Bell, CalendarClock, ChevronRight, Compass, LogOut, RefreshCw, User, Users, Heart, MessageSquare, Sparkles, CalendarDays, Monitor, X, Check, Clock, ArrowLeftRight,
} from "lucide-react";
import { api, fileUrl, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useBranding } from "@/lib/branding";
import { useLiveEvents } from "@/lib/useLiveEvents";
import { useBookingNotifications } from "@/lib/useBookingNotifications";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

/**
 * MobileCrewView — an iPhone-first crew screen.
 *
 * Three vertically-stacked sections:
 *   1. Today's bookings  — compact time-first cards, live WebSocket updates
 *   2. Guests onboard   — grouped by cabin (respecting the sort_order the
 *                          operator set on the Cabins page) with a quick
 *                          Onboard/Ashore switch each
 *   3. Live alerts      — the last ~5 booking events pushed over WS, so the
 *                          crew has a push-notification-style feed inside
 *                          the app (browser notifications still fire via
 *                          `useBookingNotifications` if permission granted)
 *
 * The page purposefully avoids the desktop admin sidebar: a compact top bar
 * + bottom tab bar owns the screen so it feels native on a phone.
 */

const REFRESH_MS = 20_000;
const ALERT_CAP = 8;

function fmtTime(iso) {
  try { return new Date(iso).toTimeString().slice(0, 5); } catch { return "—"; }
}
function isToday(iso) {
  if (!iso) return false;
  const d = new Date(iso);
  const t = new Date();
  return d.getFullYear() === t.getFullYear() && d.getMonth() === t.getMonth() && d.getDate() === t.getDate();
}
function fmtDateHeading(d) {
  return d.toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" });
}

export default function MobileCrewView() {
  const { admin, logout } = useAuth();
  const { spaName, logoUrl, crewLogoUrl } = useBranding();
  const navigate = useNavigate();
  // Photos are exposed on every crew-management surface (mobile view is
  // a crew screen). Guest-facing devices strip image_path server-side.
  const canSeePhotos = !!admin?.role;
  const notify = useBookingNotifications({ appName: "Yacht Spa" });

  const [tab, setTab] = useState("today"); // today | calendar | guests | alerts
  const [bookings, setBookings] = useState([]);
  const [weekBookings, setWeekBookings] = useState([]);
  const [weekBase, setWeekBase] = useState(() => {
    // Rolling 7-day window starting today — reads more naturally on a phone
    // than a Mon-Sun calendar week and always keeps "today" at the far left.
    const d = new Date(); d.setHours(0, 0, 0, 0); return d;
  });
  const [selectedDayIso, setSelectedDayIso] = useState(() => {
    const d = new Date(); d.setHours(0, 0, 0, 0); return d.toISOString().slice(0, 10);
  });
  const [guests, setGuests] = useState([]);
  const [alerts, setAlerts] = useState([]); // {id, kind, guest, treatment, at, tone}
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [pendingGuest, setPendingGuest] = useState(null);
  // Booking currently under inspection — tapping any row in the "Today"
  // list or the "Calendar" agenda opens a mobile-native detail dialog
  // with the same action set as the desktop CalendarPanel: reschedule,
  // (re)assign crew, confirm, cancel, delete. Sourced from `bookings`
  // OR `weekBookings` — we identify by id and always re-look-up so the
  // panel reflects the latest server state after a live event.
  const [selectedBookingId, setSelectedBookingId] = useState(null);
  const [rescheduleDraft, setRescheduleDraft] = useState("");
  const [actionBusy, setActionBusy] = useState(false);
  // Only Developer / Admin / Manager / Supervisor / Spa-Ops can mutate
  // bookings — Crew role gets a view-only detail dialog. Matches the
  // backend `_require_ops_role` boundary on `/admin/bookings/*`.
  const canManageBookings = ["developer", "admin", "manager", "supervisor", "spa_ops", "pantry"].includes(admin?.role);
  // Confirmation dialog for the guest onboard switch — same UX as the
  // desktop Guests Onboard panel. Staged as a pending toggle so cancel
  // is a true no-op (no optimistic flip until OK).
  const [pendingOnboardToggle, setPendingOnboardToggle] = useState(null);
  const latestCreatedAt = useRef(null);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true); else setRefreshing(true);
    try {
      const start = new Date(); start.setHours(0, 0, 0, 0);
      const end = new Date(); end.setHours(23, 59, 59, 999);
      const [bkRes, gsRes] = await Promise.all([
        api.get("/admin/bookings", { params: { start: start.toISOString(), end: end.toISOString(), order: "asc" } }),
        api.get("/admin/guests/onboard-list").catch(() => ({ data: [] })),
      ]);
      const bs = (bkRes.data || []).filter((b) => b.status !== "cancelled" && isToday(b.start_at));
      setBookings(bs);
      if (bs.length) {
        latestCreatedAt.current = bs.reduce((mx, b) => (b.created_at > mx ? b.created_at : mx), latestCreatedAt.current || "");
      }
      setGuests(gsRes.data || []);
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || "Could not load crew view");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(() => load(true), REFRESH_MS);
    return () => clearInterval(id);
  }, [load]);

  // Real-time booking + onboard events — booking.* pushes an alert row so
  // the crew has an in-app push feed even when browser notifications are
  // blocked. Onboard flips trigger a silent guests reload.
  useLiveEvents(useCallback((ev) => {
    if (!ev || !ev.type) return;
    if (ev.type.startsWith("booking.")) {
      const b = ev.booking || {};
      const tone = ev.type === "booking.created" ? "created"
                 : ev.type === "booking.cancelled" ? "cancelled"
                 : ev.type === "booking.deleted" ? "deleted"
                 : "updated";
      setAlerts((prev) => [{
        id: `${b.id || "ev"}-${Date.now()}`,
        kind: tone,
        guest: b.guest_name || "Guest",
        treatment: b.treatment_name || "",
        at: b.start_at || ev.at || new Date().toISOString(),
      }, ...prev].slice(0, ALERT_CAP));
      if (tone === "created") notify(b);
      // Refresh today's bookings if the event is for today.
      if (isToday(b.start_at)) load(true);
    } else if (ev.type === "guest.onboard_changed") {
      load(true);
    } else if (ev.type === "charter.updated") {
      // A manager just edited a Trip/guest — refresh so the mobile crew
      // list mirrors the yacht's rooming plan without a manual reload.
      load(true);
    }
  }, [load, notify]));

  // Stage the toggle in a confirmation dialog rather than firing straight
  // through — mirrors the desktop Guests Onboard panel so the crew has
  // the same "signed under your name" surface on every device.
  const requestOnboardToggle = (row) => {
    setPendingOnboardToggle({ row, next: !row.onboard });
  };

  const confirmOnboardToggle = async () => {
    if (!pendingOnboardToggle) return;
    const { row, next } = pendingOnboardToggle;
    setPendingOnboardToggle(null);
    setPendingGuest(row.id);
    setGuests((r) => r.map((x) => (x.id === row.id ? { ...x, onboard: next } : x)));
    try {
      await api.patch(`/admin/guests/${row.id}/onboard`, { onboard: next });
      toast.success(`${row.name} · ${next ? "Onboard" : "Ashore"} · signed by ${admin?.name || admin?.email || "you"}`);
    } catch (e) {
      setGuests((r) => r.map((x) => (x.id === row.id ? { ...x, onboard: !next } : x)));
      toast.error(formatErr(e?.response?.data?.detail) || "Could not update");
    } finally {
      setPendingGuest(null);
    }
  };

  // Final disembarkation — same endpoint as the desktop panel. Stamps the
  // guest's active stay disembark_at so they drop off the onboard roster
  // AND the Trips tab reflects the disembark date.
  const confirmFinalDisembark = async () => {
    if (!pendingOnboardToggle) return;
    const { row } = pendingOnboardToggle;
    setPendingOnboardToggle(null);
    setPendingGuest(row.id);
    try {
      await api.post(`/admin/guests/${row.id}/disembark-now`, {});
      toast.success(`${row.name} · Disembarked · signed by ${admin?.name || admin?.email || "you"}`);
      await load(true);
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || "Could not disembark");
    } finally {
      setPendingGuest(null);
    }
  };

  // Group today's bookings by hour bucket so the phone list reads like a
  // running schedule ("09:00 · 10:30 · 14:00") without needing a calendar
  // grid.
  const bookingsByHour = useMemo(() => {
    const out = new Map();
    for (const b of bookings) {
      const h = fmtTime(b.start_at).slice(0, 2) + ":00";
      if (!out.has(h)) out.set(h, []);
      out.get(h).push(b);
    }
    return Array.from(out.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [bookings]);

  // Guests grouped by cabin so the crew reads the list like the yacht's
  // rooming plan. The backend already sorts by cabin sort_order + name,
  // so we just preserve that order here.
  const guestsByCabin = useMemo(() => {
    const out = new Map();
    for (const g of guests) {
      const key = g.cabin_name || "Unassigned";
      if (!out.has(key)) out.set(key, []);
      out.get(key).push(g);
    }
    return Array.from(out.entries());
  }, [guests]);

  const totals = useMemo(() => {
    const onCount = guests.filter((g) => g.onboard).length;
    return { on: onCount, off: guests.length - onCount, total: guests.length };
  }, [guests]);

  // Week-window loader (Calendar tab). Kept separate from the daily
  // "today" loader so switching tabs feels instant — the today view
  // stays hot while the week window streams in.
  const loadWeek = useCallback(async (silent = false) => {
    try {
      const start = new Date(weekBase); start.setHours(0, 0, 0, 0);
      const end = new Date(weekBase); end.setDate(end.getDate() + 7); end.setHours(0, 0, 0, 0);
      const { data } = await api.get("/admin/bookings", { params: { start: start.toISOString(), end: end.toISOString(), order: "asc" } });
      setWeekBookings((data || []).filter((b) => b.status !== "cancelled"));
    } catch (e) {
      if (!silent) toast.error(formatErr(e?.response?.data?.detail) || "Could not load week");
    }
  }, [weekBase]);

  useEffect(() => {
    // Only fetch the week when the Calendar tab is opened for the first
    // time or the week window changes — avoids pulling 7 days of data
    // for crew who just want to see today's list.
    if (tab === "calendar") loadWeek();
  }, [tab, loadWeek]);

  const doLogout = async () => { await logout(); navigate("/admin/login"); };

  // "Desktop view" escape hatch — lets a supervisor on a tablet keep
  // using the full admin dashboard even though the auto-detect landed
  // them on the mobile view. Persisted in sessionStorage so the choice
  // sticks for the browser session but resets on next open.
  const goDesktop = () => {
    try { sessionStorage.setItem("yspa:force_desktop", "1"); } catch { /* ignore */ }
    navigate("/admin");
  };

  // Look the currently-selected booking back up from state. Doing this
  // via id (not object ref) means live events that swap booking objects
  // in place still surface the freshest data to the detail dialog.
  const selectedBooking = useMemo(() => {
    if (!selectedBookingId) return null;
    return bookings.find((b) => b.id === selectedBookingId)
      || weekBookings.find((b) => b.id === selectedBookingId)
      || null;
  }, [selectedBookingId, bookings, weekBookings]);

  // Prime the reschedule input with the booking's local start time
  // whenever a new booking is selected. Kept in an effect (not derived)
  // so operator edits to the datetime-local field persist while the
  // dialog is open, and a live event that mutates the booking doesn't
  // stomp their in-progress edit.
  useEffect(() => {
    if (!selectedBooking) { setRescheduleDraft(""); return; }
    const iso = selectedBooking.start_at;
    if (!iso) { setRescheduleDraft(""); return; }
    const d = new Date(iso);
    // datetime-local wants YYYY-MM-DDTHH:MM without offset — convert
    // to the browser's local zone first so the picker shows the same
    // time the crew is looking at.
    const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000)
      .toISOString().slice(0, 16);
    setRescheduleDraft(local);
  }, [selectedBookingId]);  // eslint-disable-line react-hooks/exhaustive-deps

  const refreshAfterMutation = async () => {
    // Refresh both windows so the caller sees their change land in
    // Today AND Calendar. Silent to avoid flashing spinners.
    await Promise.all([load(true), loadWeek(true)]);
  };

  const closeDetail = () => { if (!actionBusy) setSelectedBookingId(null); };

  const doConfirm = async () => {
    if (!selectedBooking) return;
    setActionBusy(true);
    try {
      await api.post(`/admin/bookings/${selectedBooking.id}/confirm`);
      toast.success("Booking confirmed");
      await refreshAfterMutation();
      setSelectedBookingId(null);
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || "Could not confirm");
    } finally {
      setActionBusy(false);
    }
  };

  const doReschedule = async () => {
    if (!selectedBooking || !rescheduleDraft) return;
    setActionBusy(true);
    try {
      // rescheduleDraft is a naive local-time string — mirror the desktop
      // CalendarPanel's approach and send it back as an ISO with the
      // browser's offset so the server stores the same wall-clock time.
      const local = new Date(rescheduleDraft);
      // Also patch every sibling in a couple booking (backend accepts a
      // couple move on either row today, but doing both is defensive if
      // the WebSocket ordering means we miss a broadcast).
      const ids = selectedBooking.couple_booking_id
        ? weekBookings.filter((b) => b.couple_booking_id === selectedBooking.couple_booking_id).map((b) => b.id)
        : [selectedBooking.id];
      for (const id of ids) {
        await api.put(`/admin/bookings/${id}`, { start_at: local.toISOString() });
      }
      toast.success(selectedBooking.couple_booking_id ? "Couple booking moved" : "Rescheduled");
      await refreshAfterMutation();
      setSelectedBookingId(null);
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || "Could not reschedule");
    } finally {
      setActionBusy(false);
    }
  };

  const doCancel = async () => {
    if (!selectedBooking) return;
    if (!window.confirm(`Cancel ${selectedBooking.couple_booking_id ? "both couple bookings" : "this booking"}?`)) return;
    setActionBusy(true);
    try {
      const ids = selectedBooking.couple_booking_id
        ? weekBookings.filter((b) => b.couple_booking_id === selectedBooking.couple_booking_id).map((b) => b.id)
        : [selectedBooking.id];
      for (const id of ids) {
        await api.put(`/admin/bookings/${id}`, { status: "cancelled" });
      }
      toast.success("Cancelled");
      await refreshAfterMutation();
      setSelectedBookingId(null);
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || "Could not cancel");
    } finally {
      setActionBusy(false);
    }
  };

  const doDelete = async () => {
    if (!selectedBooking) return;
    if (!window.confirm(`Delete ${selectedBooking.couple_booking_id ? "both couple bookings" : "this booking"}? This cannot be undone.`)) return;
    setActionBusy(true);
    try {
      const ids = selectedBooking.couple_booking_id
        ? weekBookings.filter((b) => b.couple_booking_id === selectedBooking.couple_booking_id).map((b) => b.id)
        : [selectedBooking.id];
      for (const id of ids) {
        await api.delete(`/admin/bookings/${id}`);
      }
      toast.success("Deleted");
      await refreshAfterMutation();
      setSelectedBookingId(null);
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || "Could not delete");
    } finally {
      setActionBusy(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-background flex flex-col"
      style={{
        paddingTop: "env(safe-area-inset-top, 0px)",
        paddingBottom: "env(safe-area-inset-bottom, 0px)",
      }}
      data-testid="mobile-crew"
    >
      {/* Compact top bar — Crew Portal brand mark + role chip + refresh
          + sign-out. The Crew Portal has its own dedicated logo (falls
          back to the spa logo when the operator hasn't uploaded a
          distinct one) so the mobile experience carries a staff-only
          brand identity separate from the guest-facing spa logo. */}
      <header className="sticky top-0 z-30 bg-card border-b border-border" data-testid="mobile-crew-header">
        <div className="flex items-center justify-between gap-3 px-4 py-3">
          <div className="flex items-center gap-2 min-w-0">
            {crewLogoUrl
              ? <img src={crewLogoUrl} alt="" className="h-7 w-7 object-contain flex-shrink-0" data-testid="crew-portal-logo" />
              : <span className="h-7 w-7 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center flex-shrink-0" data-testid="crew-portal-logo-fallback">
                  <Anchor className="h-3.5 w-3.5 text-primary" strokeWidth={1.6} />
                </span>}
            <div className="min-w-0">
              <div className="flex items-baseline gap-1.5">
                <div className="font-display text-sm leading-none truncate" data-testid="crew-portal-title">Crew Portal</div>
                {spaName && (
                  <span className="text-[9px] uppercase tracking-widest text-primary/40 leading-none truncate hidden sm:inline">
                    · {spaName}
                  </span>
                )}
              </div>
              <div className="overline text-primary/50 text-[9px] leading-none mt-0.5 truncate">
                {admin?.email} · {admin?.role}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <Button
              variant="outline" size="sm"
              onClick={goDesktop}
              className="h-8 px-2 rounded-full text-[10px] uppercase tracking-wider hidden sm:inline-flex"
              aria-label="Open desktop view"
              data-testid="mobile-crew-desktop"
              title="Open the full desktop dashboard"
            >
              <Monitor className="h-3.5 w-3.5 mr-1" /> Desktop
            </Button>
            <Button
              variant="outline" size="sm"
              onClick={() => load(true)}
              disabled={refreshing || loading}
              className="h-8 w-8 p-0 rounded-full"
              aria-label="Refresh"
              data-testid="mobile-crew-refresh"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} />
            </Button>
            <Button
              variant="outline" size="sm"
              onClick={doLogout}
              className="h-8 w-8 p-0 rounded-full"
              aria-label="Sign out"
              data-testid="mobile-crew-logout"
            >
              <LogOut className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      </header>

      <main className="flex-1 overflow-y-auto pb-24" data-testid="mobile-crew-main">
        {tab === "today" && (
          <section className="p-4 space-y-3" data-testid="mobile-crew-today">
            <div className="flex items-baseline justify-between gap-3">
              <h2 className="font-display text-2xl">Today</h2>
              <span className="text-xs text-muted-foreground">{fmtDateHeading(new Date())}</span>
            </div>
            {loading ? (
              <div className="text-sm text-muted-foreground">Loading…</div>
            ) : bookings.length === 0 ? (
              <div className="border border-dashed border-border p-6 text-center text-sm text-muted-foreground" data-testid="mobile-crew-today-empty">
                <Sparkles className="h-5 w-5 mx-auto mb-2 text-accent/60" strokeWidth={1.4} />
                No bookings for today yet.
              </div>
            ) : (
              <div className="space-y-4">
                {bookingsByHour.map(([hour, list]) => (
                  <div key={hour} data-testid={`mobile-crew-hour-${hour}`}>
                    <div className="overline text-primary/50 text-[10px] mb-2">{hour}</div>
                    <ul className="space-y-2">
                      {list.map((b) => (
                        <li
                          key={b.id}
                          role="button"
                          tabIndex={0}
                          onClick={() => setSelectedBookingId(b.id)}
                          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setSelectedBookingId(b.id); } }}
                          className={`p-3 flex items-start gap-3 border cursor-pointer hover:bg-muted/40 active:bg-muted/60 transition-colors ${b.couple_booking_id ? "border-accent/40 bg-accent/5" : "border-border bg-card"}`}
                          data-testid={`mobile-crew-booking-${b.id}`}
                        >
                          <div className="text-center min-w-[54px]">
                            <div className="font-display text-xl leading-none text-primary">{fmtTime(b.start_at)}</div>
                            <div className="text-[9px] uppercase tracking-widest text-muted-foreground mt-1">{b.duration_min}m</div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              {canSeePhotos && b.guest_image_path ? (
                                <img
                                  src={fileUrl(b.guest_image_path)}
                                  alt=""
                                  loading="lazy"
                                  decoding="async"
                                  className="w-5 h-5 rounded-full object-cover border border-border/60"
                                />
                              ) : (
                                <span className="w-5 h-5 rounded-full bg-muted border border-border/60 flex items-center justify-center">
                                  <User className="h-3 w-3 text-muted-foreground" />
                                </span>
                              )}
                              <span className="font-medium text-sm truncate">{b.guest_name}</span>
                              {b.couple_booking_id && (
                                <Badge className="text-[9px] bg-accent/20 text-accent border-0 hover:bg-accent/20">
                                  <Heart className="h-2.5 w-2.5 mr-0.5" /> Couple
                                </Badge>
                              )}
                            </div>
                            <div className="text-xs text-muted-foreground mt-0.5 truncate">{b.treatment_name}</div>
                            {b.crew_name && (
                              <div className="text-[11px] text-primary/60 mt-1">with {b.crew_name}</div>
                            )}
                            {b.notes && (
                              <div className="mt-1.5 flex items-start gap-1 text-[11px] text-muted-foreground">
                                <MessageSquare className="h-3 w-3 mt-0.5 flex-shrink-0 text-accent" />
                                <span className="whitespace-pre-wrap line-clamp-2">{b.notes}</span>
                              </div>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        {tab === "guests" && (
          <section className="p-4 space-y-3" data-testid="mobile-crew-guests">
            <div className="flex items-baseline justify-between gap-3">
              <h2 className="font-display text-2xl">Guests onboard</h2>
              {guests.length > 0 && (
                <span className="text-xs text-muted-foreground" data-testid="mobile-crew-guests-totals">
                  <span className="text-emerald-600 dark:text-emerald-400 font-medium">{totals.on}</span>
                  {" on · "}
                  <span className="text-amber-600 dark:text-amber-500 font-medium">{totals.off}</span>
                  {" ashore"}
                </span>
              )}
            </div>
            {loading ? (
              <div className="text-sm text-muted-foreground">Loading…</div>
            ) : guests.length === 0 ? (
              <div className="border border-dashed border-border p-6 text-center text-sm text-muted-foreground" data-testid="mobile-crew-guests-empty">
                <Anchor className="h-5 w-5 mx-auto mb-2 text-accent/60" strokeWidth={1.4} />
                No live charter — the guest list appears once a trip is under way.
              </div>
            ) : (
              <div className="space-y-4">
                {guestsByCabin.map(([cabin, list]) => (
                  <div key={cabin} data-testid={`mobile-crew-cabin-${cabin}`}>
                    <div className="overline text-primary/50 text-[10px] mb-2 flex items-center gap-1.5">
                      <Compass className="h-3 w-3" /> {cabin}
                      <span className="text-muted-foreground normal-case tracking-normal">· {list.length} guest{list.length === 1 ? "" : "s"}</span>
                    </div>
                    <ul className="space-y-2">
                      {list.map((g) => (
                        <li
                          key={g.id}
                          className={`p-3 flex items-center gap-3 border ${g.onboard ? "border-emerald-500/30 bg-emerald-500/5" : "border-amber-500/40 bg-amber-500/5"}`}
                          data-testid={`mobile-crew-guest-${g.id}`}
                        >
                          {canSeePhotos && g.image_path ? (
                            <img
                              src={fileUrl(g.image_path)}
                              alt={g.name}
                              loading="lazy"
                              decoding="async"
                              className="w-10 h-10 rounded-full object-cover flex-shrink-0 border border-border"
                            />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center flex-shrink-0 border border-border">
                              <User className="h-4 w-4 text-muted-foreground" strokeWidth={1.4} />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium truncate">{g.name}</div>
                            <div className="text-[11px] text-muted-foreground mt-0.5 flex items-center gap-2 flex-wrap">
                              {g.age != null && <span>Age {g.age}</span>}
                              <Badge
                                variant="outline"
                                className={`text-[9px] ${g.onboard ? "border-emerald-500/40 text-emerald-700 dark:text-emerald-400" : "border-amber-500/50 text-amber-700 dark:text-amber-500"}`}
                              >
                                {g.onboard ? "Onboard" : "Ashore"}
                              </Badge>
                            </div>
                          </div>
                          <Switch
                            checked={g.onboard}
                            onCheckedChange={() => requestOnboardToggle(g)}
                            disabled={pendingGuest === g.id}
                            data-testid={`mobile-crew-guest-switch-${g.id}`}
                            aria-label={`Toggle onboard for ${g.name}`}
                          />
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        {tab === "calendar" && (
          <section className="p-4 space-y-3" data-testid="mobile-crew-calendar">
            <div className="flex items-baseline justify-between gap-3">
              <h2 className="font-display text-2xl">Calendar</h2>
              <button
                type="button"
                onClick={() => {
                  const d = new Date(); d.setHours(0, 0, 0, 0);
                  setWeekBase(d);
                  setSelectedDayIso(d.toISOString().slice(0, 10));
                }}
                className="overline text-primary/60 text-[10px] hover:text-accent transition-colors"
                data-testid="mobile-cal-today-jump"
              >
                Jump to today
              </button>
            </div>

            {/* Horizontal-scroll date strip — 7 days from weekBase. */}
            <div
              className="flex gap-2 overflow-x-auto -mx-4 px-4 pb-1"
              style={{ scrollbarWidth: "none" }}
              data-testid="mobile-cal-strip"
            >
              {Array.from({ length: 7 }, (_, i) => {
                const d = new Date(weekBase); d.setDate(d.getDate() + i);
                const iso = d.toISOString().slice(0, 10);
                const isActive = iso === selectedDayIso;
                const isToday_ = iso === new Date().toISOString().slice(0, 10);
                const dayCount = weekBookings.filter((b) => (b.start_at || "").slice(0, 10) === iso).length;
                return (
                  <button
                    key={iso}
                    type="button"
                    onClick={() => setSelectedDayIso(iso)}
                    className={`flex-shrink-0 flex flex-col items-center justify-center min-w-[54px] py-2 px-1 border transition-colors ${
                      isActive
                        ? "border-accent bg-accent/10 text-accent"
                        : isToday_
                          ? "border-primary/40 bg-card"
                          : "border-border bg-card hover:bg-muted/40"
                    }`}
                    data-testid={`mobile-cal-day-${iso}`}
                    aria-pressed={isActive}
                  >
                    <span className="text-[9px] uppercase tracking-widest text-muted-foreground">
                      {d.toLocaleDateString(undefined, { weekday: "short" })}
                    </span>
                    <span className="font-display text-xl leading-none mt-1">{d.getDate()}</span>
                    <span className="text-[9px] mt-1 flex items-center gap-1">
                      {dayCount > 0 ? (
                        <span className="inline-flex items-center justify-center min-w-[18px] h-4 px-1 rounded-full bg-accent/20 text-accent text-[9px]" data-testid={`mobile-cal-day-${iso}-count`}>{dayCount}</span>
                      ) : <span className="opacity-30">·</span>}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Selected day heading + week nav */}
            <div className="flex items-center justify-between gap-2 pt-1">
              <button
                type="button"
                onClick={() => {
                  const d = new Date(weekBase); d.setDate(d.getDate() - 7); setWeekBase(d);
                }}
                className="overline text-primary/60 text-[10px] px-2 py-1 hover:text-accent"
                data-testid="mobile-cal-week-prev"
              >‹ Prev week</button>
              <span className="text-[11px] text-muted-foreground">
                {fmtDateHeading(new Date(selectedDayIso + "T00:00:00"))}
              </span>
              <button
                type="button"
                onClick={() => {
                  const d = new Date(weekBase); d.setDate(d.getDate() + 7); setWeekBase(d);
                }}
                className="overline text-primary/60 text-[10px] px-2 py-1 hover:text-accent"
                data-testid="mobile-cal-week-next"
              >Next week ›</button>
            </div>

            {/* Agenda for the selected day — same card design as Today. */}
            {(() => {
              const dayList = weekBookings
                .filter((b) => (b.start_at || "").slice(0, 10) === selectedDayIso)
                .sort((a, b) => (a.start_at || "").localeCompare(b.start_at || ""));
              if (dayList.length === 0) {
                return (
                  <div className="border border-dashed border-border p-6 text-center text-sm text-muted-foreground" data-testid="mobile-cal-empty">
                    <Sparkles className="h-5 w-5 mx-auto mb-2 text-accent/60" strokeWidth={1.4} />
                    No bookings this day.
                  </div>
                );
              }
              return (
                <ul className="space-y-2" data-testid="mobile-cal-list">
                  {dayList.map((b) => (
                    <li
                      key={b.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => setSelectedBookingId(b.id)}
                      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setSelectedBookingId(b.id); } }}
                      className={`p-3 flex items-start gap-3 border cursor-pointer hover:bg-muted/40 active:bg-muted/60 transition-colors ${b.couple_booking_id ? "border-accent/40 bg-accent/5" : "border-border bg-card"}`}
                      data-testid={`mobile-cal-booking-${b.id}`}
                    >
                      <div className="text-center min-w-[54px]">
                        <div className="font-display text-xl leading-none text-primary">{fmtTime(b.start_at)}</div>
                        <div className="text-[9px] uppercase tracking-widest text-muted-foreground mt-1">{b.duration_min}m</div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm truncate flex items-center gap-1.5">
                          {b.guest_name}
                          {b.couple_booking_id && <Heart className="h-3 w-3 text-accent shrink-0" />}
                        </div>
                        <div className="text-xs text-muted-foreground truncate">{b.treatment_name}</div>
                        {b.crew_name && <div className="text-[10px] uppercase tracking-widest text-primary/50 mt-0.5 truncate">{b.crew_name}</div>}
                      </div>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full uppercase tracking-widest shrink-0 ${
                        b.status === "confirmed" ? "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400" :
                        b.status === "pending" ? "bg-amber-500/15 text-amber-600 dark:text-amber-400" :
                        "bg-muted text-muted-foreground"
                      }`}>{b.status || "—"}</span>
                    </li>
                  ))}
                </ul>
              );
            })()}
          </section>
        )}

        {tab === "alerts" && (
          <section className="p-4 space-y-3" data-testid="mobile-crew-alerts">
            <div className="flex items-baseline justify-between gap-3">
              <h2 className="font-display text-2xl">Alerts</h2>
              <span className="text-xs text-muted-foreground">Live · last {ALERT_CAP}</span>
            </div>
            {alerts.length === 0 ? (
              <div className="border border-dashed border-border p-6 text-center text-sm text-muted-foreground" data-testid="mobile-crew-alerts-empty">
                <Bell className="h-5 w-5 mx-auto mb-2 text-accent/60" strokeWidth={1.4} />
                No alerts yet — new bookings and cancellations will appear here in real time.
              </div>
            ) : (
              <ul className="space-y-2">
                {alerts.map((a) => (
                  <li
                    key={a.id}
                    className={`p-3 border flex items-start gap-3 ${
                      a.kind === "created" ? "border-emerald-500/40 bg-emerald-500/5" :
                      a.kind === "cancelled" ? "border-amber-500/40 bg-amber-500/5" :
                      a.kind === "deleted" ? "border-destructive/40 bg-destructive/5" :
                      "border-border bg-card"
                    }`}
                    data-testid={`mobile-crew-alert-${a.id}`}
                  >
                    <div className="w-8 h-8 rounded-full bg-background/70 border border-border flex items-center justify-center flex-shrink-0">
                      <CalendarClock className="h-4 w-4 text-primary/70" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">
                        {a.kind === "created" && "New booking · "}
                        {a.kind === "cancelled" && "Cancelled · "}
                        {a.kind === "deleted" && "Removed · "}
                        {a.kind === "updated" && "Updated · "}
                        {a.guest}
                      </div>
                      <div className="text-xs text-muted-foreground truncate">
                        {a.treatment}{a.at ? ` · ${new Date(a.at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}` : ""}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
            <div className="pt-2 text-[11px] text-muted-foreground italic">
              Tip · allow notifications on this device so alerts pop up even when the app isn't in front.
            </div>
          </section>
        )}
      </main>

      {/* Bottom tab bar — the mobile-native way to switch between the three
          crew focus areas. `env(safe-area-inset-bottom)` keeps the iOS
          home-indicator clear on iPhones. */}
      <nav
        className="fixed bottom-0 left-0 right-0 z-30 bg-card border-t border-border"
        style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
        data-testid="mobile-crew-tabbar"
      >
        <div className="grid grid-cols-4">
          {[
            { key: "today",    label: "Today",    icon: CalendarClock, count: bookings.length },
            { key: "calendar", label: "Calendar", icon: CalendarDays,  count: 0 },
            { key: "guests",   label: "Guests",   icon: Users,         count: totals.on },
            { key: "alerts",   label: "Alerts",   icon: Bell,          count: alerts.length },
          ].map((t) => {
            const Icon = t.icon;
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                className={`flex flex-col items-center justify-center gap-1 py-2.5 transition-colors ${active ? "text-accent" : "text-primary/60 hover:text-primary"}`}
                data-testid={`mobile-crew-tab-${t.key}`}
                aria-pressed={active}
              >
                <span className="relative">
                  <Icon className="h-5 w-5" strokeWidth={1.5} />
                  {t.count > 0 && (
                    <span
                      className="absolute -top-1.5 -right-2 min-w-[16px] h-4 px-1 rounded-full bg-accent text-accent-foreground text-[9px] font-medium flex items-center justify-center"
                      data-testid={`mobile-crew-tab-${t.key}-count`}
                    >
                      {t.count > 99 ? "99+" : t.count}
                    </span>
                  )}
                </span>
                <span className="text-[10px] font-medium">{t.label}</span>
              </button>
            );
          })}
        </div>
      </nav>

      {/* Booking detail dialog — opens from either the Today list or the
          Calendar agenda. Read-only for Crew; mutations gated on
          `canManageBookings`. Actions match the desktop CalendarPanel
          set: Confirm, Reschedule (datetime-local), Cancel, Delete. */}
      <Dialog open={!!selectedBooking} onOpenChange={(o) => { if (!o) closeDetail(); }}>
        <DialogContent className="max-w-md max-h-[92vh] overflow-y-auto p-4 sm:p-6" data-testid="mobile-booking-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Booking details</DialogTitle>
          </DialogHeader>
          {selectedBooking && (
            <div className="space-y-4">
              {/* Time · treatment · guest header */}
              <div className={`p-3 border ${selectedBooking.couple_booking_id ? "border-accent/40 bg-accent/5" : "border-border bg-muted/40"}`}>
                <div className="flex items-start gap-3">
                  <div className="text-center min-w-[54px]">
                    <div className="font-display text-2xl leading-none text-primary" data-testid="mobile-detail-time">{fmtTime(selectedBooking.start_at)}</div>
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1">{selectedBooking.duration_min}m</div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                      {new Date(selectedBooking.start_at).toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" })}
                    </div>
                    <div className="font-display text-lg leading-tight mt-0.5" data-testid="mobile-detail-guest">
                      {selectedBooking.guest_name}
                      {selectedBooking.couple_booking_id && <Heart className="inline h-3.5 w-3.5 ml-1 text-accent" />}
                    </div>
                    <div className="text-sm text-muted-foreground mt-0.5 truncate">{selectedBooking.treatment_name}</div>
                    {selectedBooking.crew_name && (
                      <div className="text-[11px] uppercase tracking-widest text-primary/60 mt-1">with {selectedBooking.crew_name}</div>
                    )}
                  </div>
                  <span
                    className={`text-[10px] px-2 py-0.5 rounded-full uppercase tracking-widest shrink-0 self-start ${
                      selectedBooking.status === "confirmed" ? "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400" :
                      selectedBooking.status === "pending" ? "bg-amber-500/15 text-amber-600 dark:text-amber-400" :
                      selectedBooking.status === "cancelled" ? "bg-destructive/15 text-destructive" :
                      "bg-muted text-muted-foreground"
                    }`}
                    data-testid="mobile-detail-status"
                  >
                    {selectedBooking.status || "—"}
                  </span>
                </div>
                {selectedBooking.notes && (
                  <div className="mt-3 flex items-start gap-2 text-xs text-muted-foreground border-l-2 border-accent pl-2 py-1">
                    <MessageSquare className="h-3 w-3 mt-0.5 text-accent shrink-0" />
                    <span className="whitespace-pre-wrap">{selectedBooking.notes}</span>
                  </div>
                )}
              </div>

              {canManageBookings && selectedBooking.status !== "cancelled" && (
                <>
                  <div>
                    <label className="overline text-primary/60 text-[10px] flex items-center gap-1.5">
                      <Clock className="h-3 w-3" /> Reschedule
                    </label>
                    <Input
                      type="datetime-local"
                      value={rescheduleDraft}
                      onChange={(e) => setRescheduleDraft(e.target.value)}
                      className="mt-1.5"
                      data-testid="mobile-detail-reschedule-input"
                    />
                    {selectedBooking.couple_booking_id && (
                      <p className="text-[10px] text-muted-foreground mt-1">Both guests will be moved together.</p>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-2 pt-1">
                    {selectedBooking.status !== "confirmed" && (
                      <Button
                        onClick={doConfirm}
                        disabled={actionBusy}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white"
                        data-testid="mobile-detail-confirm"
                      >
                        <Check className="h-3.5 w-3.5 mr-1.5" /> Confirm
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      onClick={doReschedule}
                      disabled={actionBusy || !rescheduleDraft}
                      className={selectedBooking.status === "confirmed" ? "col-span-2" : ""}
                      data-testid="mobile-detail-reschedule"
                    >
                      <Clock className="h-3.5 w-3.5 mr-1.5" /> Move
                    </Button>
                    <Button
                      variant="outline"
                      onClick={doCancel}
                      disabled={actionBusy}
                      className="border-amber-500/40 text-amber-600 dark:text-amber-400"
                      data-testid="mobile-detail-cancel"
                    >
                      <X className="h-3.5 w-3.5 mr-1.5" /> Cancel
                    </Button>
                    <Button
                      variant="outline"
                      onClick={doDelete}
                      disabled={actionBusy}
                      className="border-destructive/40 text-destructive"
                      data-testid="mobile-detail-delete"
                    >
                      <X className="h-3.5 w-3.5 mr-1.5" /> Delete
                    </Button>
                  </div>
                </>
              )}

              {!canManageBookings && (
                <div className="text-xs text-muted-foreground italic border border-dashed border-border p-3">
                  Your role can view booking details but not modify them. Ask a Supervisor or Manager for changes.
                </div>
              )}

              {selectedBooking.status === "cancelled" && canManageBookings && (
                <Button
                  variant="outline"
                  onClick={doDelete}
                  disabled={actionBusy}
                  className="w-full border-destructive/40 text-destructive"
                  data-testid="mobile-detail-delete-cancelled"
                >
                  <X className="h-3.5 w-3.5 mr-1.5" /> Delete cancelled booking
                </Button>
              )}

              <Button
                variant="outline"
                onClick={closeDetail}
                disabled={actionBusy}
                className="w-full"
                data-testid="mobile-detail-close"
              >
                Close
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
      {/* Onboard sign in/out confirmation — parity with the desktop
          Guests Onboard panel. Explains that the change will be logged
          under the operator's name before the switch actually flips. */}
      <AlertDialog open={!!pendingOnboardToggle} onOpenChange={(o) => { if (!o) setPendingOnboardToggle(null); }}>
        <AlertDialogContent data-testid="mobile-onboard-confirm-dialog" className="max-w-[92vw] sm:max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-xl flex items-center gap-2">
              <ArrowLeftRight className="h-5 w-5 text-accent" />
              {pendingOnboardToggle?.next
                ? `Bring ${pendingOnboardToggle?.row?.name} back onboard?`
                : `Mark ${pendingOnboardToggle?.row?.name} as ashore?`}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 pt-2">
                <p className="text-sm">
                  This will change{" "}
                  <span className="font-medium text-foreground">{pendingOnboardToggle?.row?.name}</span>'s
                  status from{" "}
                  <Badge variant="outline" className={pendingOnboardToggle?.next ? "border-amber-500/50 text-amber-700 dark:text-amber-500" : "border-emerald-500/40 text-emerald-700 dark:text-emerald-400"}>
                    {pendingOnboardToggle?.next ? "Ashore" : "Onboard"}
                  </Badge>
                  {" → "}
                  <Badge variant="outline" className={pendingOnboardToggle?.next ? "border-emerald-500/40 text-emerald-700 dark:text-emerald-400" : "border-amber-500/50 text-amber-700 dark:text-amber-500"}>
                    {pendingOnboardToggle?.next ? "Onboard" : "Ashore"}
                  </Badge>
                </p>
                <div className="text-xs text-muted-foreground border-l-2 border-accent/60 pl-3 py-1 bg-accent/5">
                  <div className="overline text-primary/60 text-[10px] mb-1">This action will be logged under</div>
                  <div className="font-medium text-foreground">
                    {admin?.name || admin?.email || "your account"}
                  </div>
                  <div className="opacity-70">{admin?.role?.toUpperCase()}</div>
                </div>
                {pendingOnboardToggle?.next === false && (
                  <div className="text-[11px] bg-amber-500/5 border border-amber-500/30 p-2.5 space-y-1">
                    <div className="text-muted-foreground leading-snug"><span className="text-foreground font-medium">Temporarily ashore</span> — kept on the roster with an Ashore badge.</div>
                    <div className="text-muted-foreground leading-snug"><span className="text-foreground font-medium">Confirm disembarkation</span> — stamps today's date on the stay and removes them from the onboard count.</div>
                  </div>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className={pendingOnboardToggle?.next === false ? "flex-col gap-2" : ""}>
            {pendingOnboardToggle?.next === false ? (
              <>
                <AlertDialogCancel data-testid="mobile-onboard-confirm-cancel" className="w-full">Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={confirmOnboardToggle}
                  className="bg-amber-600 hover:bg-amber-700 w-full"
                  data-testid="mobile-onboard-confirm-ok"
                >
                  Temporarily ashore
                </AlertDialogAction>
                <AlertDialogAction
                  onClick={confirmFinalDisembark}
                  className="bg-rose-700 hover:bg-rose-800 text-white w-full"
                  data-testid="mobile-onboard-confirm-disembark"
                >
                  <LogOut className="h-3.5 w-3.5 mr-1.5" />
                  Confirm disembarkation
                </AlertDialogAction>
              </>
            ) : (
              <>
                <AlertDialogCancel data-testid="mobile-onboard-confirm-cancel">Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={confirmOnboardToggle}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="mobile-onboard-confirm-ok"
                >
                  Yes, bring onboard
                </AlertDialogAction>
              </>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
